import React, { useState, useEffect } from 'react';
import { facturacionService } from '../../services/facturacion.service';
import { Settings, Building2, Shield, FileText, Save, TestTube, Upload, AlertCircle, CheckCircle, Info, Loader2 } from 'lucide-react';

const BillingConfiguration = () => {
  const [activeTab, setActiveTab] = useState('emisor');
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({
    emisor: {
      rfc: '',
      razonSocial: '',
      regimenFiscal: '',
      direccion: {
        calle: '',
        numeroExterior: '',
        numeroInterior: '',
        colonia: '',
        municipio: '',
        estado: '',
        codigoPostal: '',
        pais: 'México'
      }
    },
    certificados: {
      certificado: null,
      llavePrivada: null,
      password: '',
      vigenciaDesde: '',
      vigenciaHasta: '',
      numeroSerie: ''
    },
    timbrado: {
      pac: '',
      usuario: '',
      password: '',
      urlTimbrado: '',
      urlCancelacion: ''
    },
    configuracion: {
      serie: '',
      folioInicial: 1,
      folioActual: 1,
      lugarExpedicion: ''
    }
  });

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const response = await facturacionService.configuracion.get();
      setConfig(response.data);
    } catch (error) {
      console.error('Error cargando configuración:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfiguration = async (section) => {
    try {
      setLoading(true);
      const response = await facturacionService.configuracion.update(config);
      alert(`Configuración de ${section} guardada exitosamente`);
      
      // Actualizar la configuración con la respuesta del servidor
      if (response.data) {
        setConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error guardando configuración:', error);
      alert(error.response?.data?.message || 'Error al guardar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (field, file) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setConfig(prev => ({
          ...prev,
          certificados: {
            ...prev.certificados,
            [field]: {
              name: file.name,
              content: e.target.result,
              size: file.size
            }
          }
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTestCertificados = async () => {
    try {
      setLoading(true);
      const response = await facturacionService.configuracion.testCertificados({
        certificado: config.certificados.certificado?.content,
        llavePrivada: config.certificados.llavePrivada?.content,
        password: config.certificados.password
      });
      
      if (response.data.valido) {
        alert(`✅ Certificados válidos\n\nRFC: ${response.data.rfc}\nRazón Social: ${response.data.razonSocial}\nVigencia: ${new Date(response.data.vigenciaDesde).toLocaleDateString()} - ${new Date(response.data.vigenciaHasta).toLocaleDateString()}`);
      } else {
        alert('❌ Los certificados no son válidos');
      }
    } catch (error) {
      alert(`❌ Error validando certificados: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTestPAC = async () => {
    try {
      setLoading(true);
      const response = await facturacionService.configuracion.testPAC(config.timbrado);
      
      if (response.data.conexionExitosa) {
        alert(`✅ Conexión exitosa con ${response.data.pac}\n\nServicios disponibles: ${response.data.serviciosDisponibles.join(', ')}`);
      } else {
        alert('❌ No se pudo conectar con el PAC');
      }
    } catch (error) {
      alert(`❌ Error probando conexión PAC: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'emisor', label: 'Datos del Emisor', icon: Building2 },
    { id: 'certificados', label: 'Certificados', icon: Shield },
    { id: 'timbrado', label: 'Timbrado', icon: FileText },
    { id: 'configuracion', label: 'Configuración', icon: Settings }
  ];

  if (loading && activeTab === 'emisor') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-gray-900 dark:to-gray-800 p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-300">Cargando configuración...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-gray-900 dark:to-gray-800 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-white/20 dark:border-gray-700/20 shadow-xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              Configuración de Facturación
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-300 ml-11">
            Configuración de certificados de sello digital y parámetros de emisión
          </p>
        </div>
      </div>

      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-white/20 dark:border-gray-700/20 shadow-xl overflow-hidden">
        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex overflow-x-auto">
            {tabs.map(tab => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={`flex items-center gap-3 px-6 py-4 font-medium transition-all duration-200 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500 bg-primary-50/50 dark:bg-primary-900/20'
                      : 'text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <IconComponent className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6">
          {/* Datos del Emisor */}
          {activeTab === 'emisor' && (
            <div className="space-y-6">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Datos del Emisor</h2>
                <p className="text-gray-600 dark:text-gray-300">Información fiscal de tu empresa para la emisión de facturas</p>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleSaveConfiguration('emisor'); }} className="space-y-8">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Información Fiscal
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">RFC *</label>
                      <input
                        type="text"
                        value={config.emisor.rfc}
                        onChange={(e) => setConfig({
                          ...config,
                          emisor: { ...config.emisor, rfc: e.target.value.toUpperCase() }
                        })}
                        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        required
                        maxLength="13"
                        placeholder="XAXX010101000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Razón Social *</label>
                      <input
                        type="text"
                        value={config.emisor.razonSocial}
                        onChange={(e) => setConfig({
                          ...config,
                          emisor: { ...config.emisor, razonSocial: e.target.value }
                        })}
                        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        required
                        placeholder="Nombre de la empresa"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Régimen Fiscal *</label>
                    <select
                      value={config.emisor.regimenFiscal}
                      onChange={(e) => setConfig({
                        ...config,
                        emisor: { ...config.emisor, regimenFiscal: e.target.value }
                      })}
                      className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                      required
                    >
                      <option value="">Seleccionar...</option>
                      <option value="601">601 - General de Ley Personas Morales</option>
                      <option value="603">603 - Personas Morales con Fines no Lucrativos</option>
                      <option value="612">612 - Personas Físicas con Actividades Empresariales y Profesionales</option>
                      <option value="621">621 - Incorporación Fiscal</option>
                      <option value="622">622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras</option>
                    </select>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Dirección Fiscal</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Calle *</label>
                      <input
                        type="text"
                        value={config.emisor.direccion.calle}
                        onChange={(e) => setConfig({
                          ...config,
                          emisor: {
                            ...config.emisor,
                            direccion: { ...config.emisor.direccion, calle: e.target.value }
                          }
                        })}
                        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Número Exterior *</label>
                      <input
                        type="text"
                        value={config.emisor.direccion.numeroExterior}
                        onChange={(e) => setConfig({
                          ...config,
                          emisor: {
                            ...config.emisor,
                            direccion: { ...config.emisor.direccion, numeroExterior: e.target.value }
                          }
                        })}
                        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Colonia *</label>
                      <input
                        type="text"
                        value={config.emisor.direccion.colonia}
                        onChange={(e) => setConfig({
                          ...config,
                          emisor: {
                            ...config.emisor,
                            direccion: { ...config.emisor.direccion, colonia: e.target.value }
                          }
                        })}
                        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Código Postal *</label>
                      <input
                        type="text"
                        value={config.emisor.direccion.codigoPostal}
                        onChange={(e) => setConfig({
                          ...config,
                          emisor: {
                            ...config.emisor,
                            direccion: { ...config.emisor.direccion, codigoPostal: e.target.value }
                          }
                        })}
                        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        required
                        maxLength="5"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Municipio *</label>
                      <input
                        type="text"
                        value={config.emisor.direccion.municipio}
                        onChange={(e) => setConfig({
                          ...config,
                          emisor: {
                            ...config.emisor,
                            direccion: { ...config.emisor.direccion, municipio: e.target.value }
                          }
                        })}
                        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Estado *</label>
                      <input
                        type="text"
                        value={config.emisor.direccion.estado}
                        onChange={(e) => setConfig({
                          ...config,
                          emisor: {
                            ...config.emisor,
                            direccion: { ...config.emisor.direccion, estado: e.target.value }
                          }
                        })}
                        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button 
                    type="submit" 
                    className="btn btn-primary flex items-center gap-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    {loading ? 'Guardando...' : 'Guardar Configuración'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Certificados */}
          {activeTab === 'certificados' && (
            <div className="space-y-6">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Certificados de Sello Digital</h2>
                <p className="text-gray-600 dark:text-gray-300">Carga tus certificados de sello digital para poder timbrar facturas</p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-full ${
                    config.certificados.certificado 
                      ? 'bg-success-100 dark:bg-success-900/20 text-success-600 dark:text-success-400'
                      : 'bg-error-100 dark:bg-error-900/20 text-error-600 dark:text-error-400'
                  }`}>
                    {config.certificados.certificado ? (
                      <CheckCircle className="w-6 h-6" />
                    ) : (
                      <AlertCircle className="w-6 h-6" />
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Estado de Certificados</h4>
                    <p className="text-gray-600 dark:text-gray-300">
                      {config.certificados.certificado 
                        ? 'Certificados cargados correctamente' 
                        : 'No hay certificados cargados'
                      }
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleSaveConfiguration('certificados'); }} className="space-y-6">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    Archivos de Certificado
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Certificado (.cer) *</label>
                      <div className="relative">
                        <input
                          type="file"
                          accept=".cer"
                          onChange={(e) => handleFileUpload('certificado', e.target.files[0])}
                          className="hidden"
                          id="certificado"
                        />
                        <label 
                          htmlFor="certificado" 
                          className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors duration-200 bg-white dark:bg-gray-800"
                        >
                          <div className="text-center">
                            <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                              {config.certificados.certificado 
                                ? config.certificados.certificado.name
                                : 'Seleccionar archivo .cer'
                              }
                            </span>
                          </div>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Llave Privada (.key) *</label>
                      <div className="relative">
                        <input
                          type="file"
                          accept=".key"
                          onChange={(e) => handleFileUpload('llavePrivada', e.target.files[0])}
                          className="hidden"
                          id="llavePrivada"
                        />
                        <label 
                          htmlFor="llavePrivada" 
                          className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors duration-200 bg-white dark:bg-gray-800"
                        >
                          <div className="text-center">
                            <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                              {config.certificados.llavePrivada 
                                ? config.certificados.llavePrivada.name
                                : 'Seleccionar archivo .key'
                              }
                            </span>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Contraseña de la Llave Privada *</label>
                    <input
                      type="password"
                      value={config.certificados.password}
                      onChange={(e) => setConfig({
                        ...config,
                        certificados: { ...config.certificados, password: e.target.value }
                      })}
                      className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                      placeholder="Ingresa la contraseña de tu llave privada"
                      required
                    />
                  </div>
                </div>

                <div className="flex justify-between">
                  <button 
                    type="button" 
                    className="btn btn-secondary flex items-center gap-2"
                    onClick={handleTestCertificados}
                    disabled={loading || !config.certificados.certificado || !config.certificados.llavePrivada}
                  >
                    <TestTube className="w-4 h-4" />
                    Validar Certificados
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary flex items-center gap-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    {loading ? 'Guardando...' : 'Guardar Certificados'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Configuración de Timbrado */}
          {activeTab === 'timbrado' && (
            <div className="space-y-6">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Configuración de Timbrado</h2>
                <p className="text-gray-600 dark:text-gray-300">Configura tu PAC (Proveedor Autorizado de Certificación) para el timbrado</p>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-900 dark:text-blue-100">¿Qué es un PAC?</h4>
                    <p className="text-blue-700 dark:text-blue-300 text-sm mt-1">
                      Un PAC es un Proveedor Autorizado de Certificación que se encarga de validar y timbrar tus facturas electrónicas ante el SAT.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleSaveConfiguration('timbrado'); }} className="space-y-6">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Proveedor PAC *</label>
                      <select
                        value={config.timbrado.pac}
                        onChange={(e) => setConfig({
                          ...config,
                          timbrado: { ...config.timbrado, pac: e.target.value }
                        })}
                        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        required
                      >
                        <option value="">Seleccionar PAC...</option>
                        <option value="finkok">Finkok</option>
                        <option value="stamped">Stamped</option>
                        <option value="timbrado_fiscal">Timbrado Fiscal</option>
                        <option value="otro">Otro</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Usuario PAC *</label>
                        <input
                          type="text"
                          value={config.timbrado.usuario}
                          onChange={(e) => setConfig({
                            ...config,
                            timbrado: { ...config.timbrado, usuario: e.target.value }
                          })}
                          className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Contraseña PAC *</label>
                        <input
                          type="password"
                          value={config.timbrado.password}
                          onChange={(e) => setConfig({
                            ...config,
                            timbrado: { ...config.timbrado, password: e.target.value }
                          })}
                          className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">URL de Timbrado</label>
                        <input
                          type="url"
                          value={config.timbrado.urlTimbrado}
                          onChange={(e) => setConfig({
                            ...config,
                            timbrado: { ...config.timbrado, urlTimbrado: e.target.value }
                          })}
                          className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                          placeholder="https://..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">URL de Cancelación</label>
                        <input
                          type="url"
                          value={config.timbrado.urlCancelacion}
                          onChange={(e) => setConfig({
                            ...config,
                            timbrado: { ...config.timbrado, urlCancelacion: e.target.value }
                          })}
                          className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                          placeholder="https://..."
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between">
                  <button 
                    type="button" 
                    className="btn btn-secondary flex items-center gap-2"
                    onClick={handleTestPAC}
                    disabled={loading || !config.timbrado.usuario || !config.timbrado.password}
                  >
                    <TestTube className="w-4 h-4" />
                    Probar Conexión
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary flex items-center gap-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    {loading ? 'Guardando...' : 'Guardar Configuración'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Configuración General */}
          {activeTab === 'configuracion' && (
            <div className="space-y-6">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Configuración General</h2>
                <p className="text-gray-600 dark:text-gray-300">Configuración de series, folios y otros parámetros de facturación</p>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleSaveConfiguration('configuracion'); }} className="space-y-6">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Numeración de Facturas</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Serie</label>
                      <input
                        type="text"
                        value={config.configuracion.serie}
                        onChange={(e) => setConfig({
                          ...config,
                          configuracion: { ...config.configuracion, serie: e.target.value.toUpperCase() }
                        })}
                        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        placeholder="A, B, FAC, etc."
                        maxLength="10"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Folio Inicial *</label>
                      <input
                        type="number"
                        value={config.configuracion.folioInicial}
                        onChange={(e) => setConfig({
                          ...config,
                          configuracion: { ...config.configuracion, folioInicial: parseInt(e.target.value) || 1 }
                        })}
                        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        min="1"
                        required
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Folio Actual</label>
                    <input
                      type="number"
                      value={config.configuracion.folioActual}
                      onChange={(e) => setConfig({
                        ...config,
                        configuracion: { ...config.configuracion, folioActual: parseInt(e.target.value) || 1 }
                      })}
                      className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-xl cursor-not-allowed"
                      min="1"
                      readOnly
                    />
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      Este valor se actualiza automáticamente con cada factura emitida
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Lugar de Expedición</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Código Postal de Expedición *</label>
                    <input
                      type="text"
                      value={config.configuracion.lugarExpedicion}
                      onChange={(e) => setConfig({
                        ...config,
                        configuracion: { ...config.configuracion, lugarExpedicion: e.target.value }
                      })}
                      className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                      placeholder="06000"
                      maxLength="5"
                      required
                    />
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      Código postal donde se expiden las facturas
                    </p>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button 
                    type="submit" 
                    className="btn btn-primary flex items-center gap-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    {loading ? 'Guardando...' : 'Guardar Configuración'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingConfiguration;